import React, { useEffect, useRef, useState } from "react";
import { EyeOff, Loader2, Send, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { avatarSrc } from "@/lib/defaults";
import { PRAYER_ACCENT, PRAYER_ACCENT_SOFT, QUICK_ENCOURAGEMENTS, timeAgo } from "@/lib/prayers";
import { VerifiedBadge } from "@/components/VerifiedBadge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Textarea } from "@/components/ui/textarea";

const EncouragementRow = ({ c, canDelete, onDelete, lang, t }) => (
  <li className="flex items-start gap-2.5" data-testid={`prayer-encouragement-${c.id}`}>
    {c.author?.anonymous ? (
      <span className="h-8 w-8 rounded-full grid place-items-center shrink-0" style={{ background: "var(--yalola-surface-alt)" }}><EyeOff size={14} style={{ color: "var(--yalola-text-muted)" }} /></span>
    ) : (
      <Avatar className="h-8 w-8 shrink-0"><AvatarImage src={avatarSrc(c.author, 32)} /><AvatarFallback className="text-[10px]">{c.author?.name?.[0]}</AvatarFallback></Avatar>
    )}
    <div className="min-w-0 flex-1 rounded-2xl px-3 py-2" style={{ background: "var(--yalola-surface-alt)" }}>
      <div className="flex items-center gap-1.5 text-xs">
        <span className="font-semibold truncate">{c.author?.name}</span>
        {!c.author?.anonymous && <VerifiedBadge user={c.author} size={11} />}
        {c.is_author && <span className="chip text-[9px] px-1.5 py-0" style={{ background: PRAYER_ACCENT_SOFT, color: PRAYER_ACCENT, borderColor: "transparent" }}>{t("prayers.author_chip", "Auteur")}</span>}
        <span style={{ color: "var(--yalola-text-muted)" }}>· {timeAgo(c.created_at, lang)}</span>
        <span className="flex-1" />
        {canDelete && (
          <button type="button" onClick={() => onDelete(c)} className="opacity-60 hover:opacity-100" aria-label={t("common.delete", "Supprimer")} data-testid={`prayer-encouragement-delete-${c.id}`}><Trash2 size={12} /></button>
        )}
      </div>
      <p className="text-sm leading-snug whitespace-pre-wrap mt-0.5" style={{ color: "var(--yalola-text)" }}>{c.content}</p>
    </div>
  </li>
);

// Encouragement thread under a prayer: list + one-tap phrases/verses + free text.
export const PrayerEncouragements = ({ p, user, canManage, onCount }) => {
  const { t, lang } = useApp();
  const [items, setItems] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    api.get(`/prayers/${p.id}/comments`).then(({ data }) => setItems(data.comments || [])).catch(() => setItems([]));
  }, [p.id]);

  const send = async (content) => {
    const body = (content || "").trim();
    if (!body || sending) return;
    setSending(true);
    try {
      const { data } = await api.post(`/prayers/${p.id}/comments`, { content: body });
      setItems((prev) => [...(prev || []), data.comment]);
      setText("");
      onCount?.(data.comment_count);
      toast.success(t("prayers.encouraged", "Encouragement envoyé 💚"));
    } catch (e) { toast.error(e?.friendlyMessage || t("common.error", "Erreur")); }
    setSending(false);
  };

  const remove = async (c) => {
    if (!window.confirm(t("prayers.delete_comment_confirm", "Supprimer ce message ?"))) return;
    try {
      const { data } = await api.delete(`/prayers/${p.id}/comments/${c.id}`);
      setItems((prev) => (prev || []).filter((x) => x.id !== c.id));
      onCount?.(data.comment_count);
    } catch { toast.error(t("common.error", "Erreur")); }
  };

  return (
    <div className="space-y-3 pt-2 border-t" style={{ borderColor: "var(--yalola-border)" }} data-testid={`prayer-encouragements-${p.id}`}>
      {items === null && <div className="py-3 grid place-items-center"><Loader2 size={18} className="animate-spin" style={{ color: PRAYER_ACCENT }} /></div>}
      {items && items.length === 0 && <p className="text-xs" style={{ color: "var(--yalola-text-muted)" }} data-testid={`prayer-encouragements-empty-${p.id}`}>{t("prayers.encourage_empty", "Sois le premier à laisser un mot d'encouragement.")}</p>}
      {items && items.length > 0 && (
        <ul className="space-y-2">
          {items.map((c) => <EncouragementRow key={c.id} c={c} lang={lang} t={t} canDelete={!!user && (c.is_mine || canManage)} onDelete={remove} />)}
        </ul>
      )}
      {user && (
        <div className="space-y-2">
          <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1" data-testid={`prayer-quick-${p.id}`}>
            {QUICK_ENCOURAGEMENTS.map((q, i) => (
              <button key={q.key} type="button" disabled={sending} onClick={() => send(t(q.key, q.fb))}
                className="chip shrink-0 text-xs hover:bg-[color:var(--yalola-primary-soft)] transition-colors" data-testid={`prayer-quick-${i + 1}-${p.id}`}>
                {t(q.key, q.fb)}
              </button>
            ))}
          </div>
          <div className="flex items-end gap-2">
            <Textarea ref={inputRef} rows={1} value={text} maxLength={500} onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(text); } }}
              placeholder={t("prayers.encourage_ph", "Écris un mot d'encouragement…")} className="min-h-[40px] resize-none rounded-2xl" data-testid={`prayer-encourage-input-${p.id}`} />
            <button type="button" onClick={() => send(text)} disabled={sending || !text.trim()} aria-label={t("prayers.encourage_send", "Envoyer")}
              className="h-10 w-10 rounded-full grid place-items-center shrink-0 disabled:opacity-40" style={{ background: PRAYER_ACCENT, color: "#fff" }} data-testid={`prayer-encourage-send-${p.id}`}>
              {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
